/**
 * Профиль ученика для интерфейса: приветствие, ступень CEFR и «лучшее
 * следующее действие».
 *
 * Всё здесь — чистые функции над уже существующими сигналами стора, как в
 * `forecastService`: вход — числа и журнал, выход не зависит ни от сети,
 * ни от текущего времени, кроме явно переданного `now`. Поэтому адаптацию
 * интерфейса под ученика можно проверить без React.
 *
 * Стратегия обучения (`INDUCTIVE`/`DEDUCTIVE`) сюда намеренно не попадает:
 * это скрытый профиль. Наружу выходят только следствия — формат занятия и
 * подсказка, что делать дальше.
 */

import i18n from '../i18n';

/** Сигналы профиля, влияющие на выбор действия. */
export interface ProfileSignals {
  readonly completedLessonIds: readonly string[];
  readonly activeVocabulary: readonly unknown[];
  /**
   * Куда ведёт «продолжить учиться» — экран программы. Передаётся сюда,
   * потому что сервис не знает о маршрутах приложения. `null` — вести некуда.
   */
  readonly nextLessonRoute: string | null;
}

/** Предложенное действие: подпись кнопки, маршрут и объяснение. */
export interface NextAction {
  readonly label: string;
  readonly route: string;
  readonly hint: string;
}

/**
 * Ступень CEFR по уровню модели. Границы согласованы с `CEFR_TARGETS` из
 * `forecastService` (A2 — 2, B1 — 3, B2 — 4): одна ступень — одна единица.
 */
export function cefrLabel(level: number): string {
  if (level < 2) return 'A1';
  if (level < 3) return 'A2';
  if (level < 4) return 'B1';
  if (level < 5) return 'B2';
  if (level < 6) return 'C1';
  return 'C2';
}

/** Приветствие по времени суток: короткое, серия живёт в чипе шапки. */
export function greeting(now: Date = new Date()): string {
  const hour = now.getHours();
  if (hour < 6) return i18n.t('greeting.night');
  if (hour < 12) return i18n.t('greeting.morning');
  if (hour < 18) return i18n.t('greeting.day');
  return i18n.t('greeting.evening');
}

/**
 * Лучшее следующее действие — упорядоченный выбор по состоянию ученика.
 * Порядок не случаен: сначала закрыть самый крупный пробел (первый урок),
 * затем продолжить программу, затем закрепление слов из кармана. Дневного
 * потолка нет: ученик идёт столько, сколько хочет. Подсказка объясняет
 * выбор, чтобы кнопка не выглядела произвольной.
 *
 * Маршруты ведут на другой экран, а не на текущий: карточка живёт на
 * дашборде, поэтому `route` обязан быть уроком или чатом — иначе переход
 * в тот же путь ничего не делает и кнопка становится пустышкой.
 */
export function nextBestAction(signals: ProfileSignals): NextAction {
  const lessonRoute = signals.nextLessonRoute;

  if (lessonRoute !== null && signals.completedLessonIds.length === 0) {
    return {
      label: i18n.t('nextAction.firstLesson.label'),
      route: lessonRoute,
      hint: i18n.t('nextAction.firstLesson.hint'),
    };
  }

  if (lessonRoute !== null) {
    return {
      label: i18n.t('nextAction.continueLesson.label'),
      route: lessonRoute,
      hint: i18n.t('nextAction.continueLesson.hint'),
    };
  }

  if (signals.activeVocabulary.length > 0) {
    return {
      label: i18n.t('nextAction.chat.label'),
      route: '/chat',
      hint: i18n.t('nextAction.chat.hint'),
    };
  }

  // Каталог пуст — вести на урок некуда, но действие всё равно осмысленное.
  return {
    label: i18n.t('nextAction.progress.label'),
    route: '/progress',
    hint: i18n.t('nextAction.progress.hint'),
  };
}
